import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, MessageCircle, Gift, Share2, Link2, Facebook, Sparkles } from "lucide-react";
import { PageShell } from "../components/PageShell";
import { Reveal, MaskLines } from "../components/Reveal";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { useLang, WHATSAPP } from "../lib/i18n";
import { getSeason } from "../lib/season";
import { useSeo } from "../lib/seo";

const ease = [0.22, 1, 0.36, 1];
const API = process.env.REACT_APP_BACKEND_URL;

const COPY = {
  it: {
    kicker: "Idea regalo · Centro estetico a Catanzaro",
    h1: { xmas: "Il regalo di Natale che fa brillare la pelle", valentine: "Il regalo di San Valentino che fa brillare la pelle", none: "Il regalo che fa brillare la pelle" },
    lead: "Pacchetto Glow di Gioia Centro Estetico: una seduta di Mesoporazione estetica in cabina e la crema viso arancia e vitamina C Gioia Skincare da portare a casa. Un buono regalo elegante, da regalare a Catanzaro a chi ami.",
    give: "Regala il Pacchetto Glow", wa: "Chiedi a Gioia su WhatsApp",
    inside: "Cosa c'è dentro",
    items: [
      { t: "Mesoporazione estetica", d: "La \"mesoterapia senza aghi\": impulsi delicati aiutano gli attivi a penetrare in profondità per una pelle più idratata, tonica e luminosa. Nessun fastidio, nessun tempo di recupero.", to: "/trattamenti/mesoporazione-estetica", l: "Scopri il trattamento", img: "/images/mesoporazione-estetica.jpg" },
      { t: "Crema viso arancia e vitamina C", d: "La crema Gioia Skincare per prolungare a casa l'effetto luminoso: ogni mattina, un gesto semplice per un incarnato più radioso.", to: "/shop", l: "Vedi nello shop", img: "/images/crema-vitc-hero.jpg" },
    ],
    how: "Come regalarlo",
    steps: [
      ["Scegli il Pacchetto Glow", "Seleziona il pacchetto, l'importo e scrivi una dedica per chi lo riceve."],
      ["Paga online in sicurezza", "Il pagamento avviene con carta su Stripe, in pochi secondi."],
      ["Ricevi il buono", "Ricevi subito il codice del buono regalo, pronto da stampare o inviare."],
      ["Lei prenota da Gioia", "Chi riceve il regalo prenota la seduta in Via Tre Fontane 37, Catanzaro."],
    ],
    whyT: "Un'idea regalo a Catanzaro, per ogni occasione",
    why: [
      "Cerchi un regalo di Natale originale a Catanzaro? Un buono regalo per un centro estetico è un pensiero che si vive, non si dimentica in un cassetto. Il Pacchetto Glow unisce un trattamento viso professionale a un prodotto skincare da usare ogni giorno.",
      "È perfetto anche come regalo di San Valentino per lei, per un compleanno, per la festa della mamma o per ringraziare un'amica speciale. Gioia ti accoglie nel suo centro estetico di Catanzaro, in Via Tre Fontane 37.",
    ],
    tags: ["Regalo di Natale Catanzaro", "Regalo San Valentino per lei", "Buono regalo centro estetico", "Trattamento viso Catanzaro", "Mesoporazione Catanzaro", "Idea regalo donna"],
    faqT: "Domande frequenti",
    faq: [
      ["Che cosa comprende il Pacchetto Glow?", "Una seduta di Mesoporazione estetica e la crema viso arancia e vitamina C della linea Gioia Skincare."],
      ["Come ricevo il buono regalo?", "Dopo il pagamento online ricevi subito il codice del buono, con il nome di chi lo riceve e la tua dedica."],
      ["Dove si usa il buono?", "Presso Gioia Centro Estetico, in Via Tre Fontane 37 a Catanzaro. Chi lo riceve può prenotare online o su WhatsApp."],
      ["Posso scegliere un altro trattamento?", "Certo: nella sezione buoni regalo puoi scegliere qualsiasi trattamento oppure lasciare la scelta a chi lo riceve."],
    ],
    shareT: "Condividi il Pacchetto Glow", shareS: "Mandalo a chi deve farti un regalo, o a un'amica che cerca un'idea.",
    copy: "Copia link", copied: "Link copiato", share: "Condividi",
    shareText: "Guarda il Pacchetto Glow di Gioia Centro Estetico a Catanzaro: Mesoporazione + crema vitamina C, l'idea regalo perfetta!",
    other: "Tutti i buoni regalo",
    seoT: { xmas: "Regalo di Natale a Catanzaro · Pacchetto Glow · Gioia", valentine: "Regalo San Valentino a Catanzaro · Pacchetto Glow · Gioia", none: "Idea regalo a Catanzaro · Pacchetto Glow · Gioia" },
    seoD: "Pacchetto Glow: Mesoporazione estetica + crema vitamina C in un buono regalo. L'idea regalo per Natale e San Valentino dal centro estetico Gioia a Catanzaro.",
  },
  en: {
    kicker: "Gift idea · Beauty centre in Catanzaro",
    h1: { xmas: "The Christmas gift that makes skin glow", valentine: "The Valentine's gift that makes skin glow", none: "The gift that makes skin glow" },
    lead: "The Glow Package from Gioia Centro Estetico: an aesthetic mesoporation session in the salon and the Gioia Skincare orange & vitamin C face cream to take home. An elegant gift card for someone you love in Catanzaro.",
    give: "Give the Glow Package", wa: "Ask Gioia on WhatsApp",
    inside: "What's inside",
    items: [
      { t: "Aesthetic mesoporation", d: "\"Needle-free mesotherapy\": gentle pulses help actives penetrate deeper for more hydrated, toned and radiant skin. No discomfort, no downtime.", to: "/trattamenti/mesoporazione-estetica", l: "Discover the treatment", img: "/images/mesoporazione-estetica.jpg" },
      { t: "Orange & vitamin C face cream", d: "The Gioia Skincare cream that extends the glow at home: every morning, a simple ritual for a more radiant complexion.", to: "/shop", l: "See it in the shop", img: "/images/crema-vitc-hero.jpg" },
    ],
    how: "How to give it",
    steps: [
      ["Choose the Glow Package", "Select the package, the amount and write a message for the recipient."],
      ["Pay securely online", "Pay by card with Stripe in a few seconds."],
      ["Receive the gift card", "You instantly receive the gift card code, ready to print or send."],
      ["She books at Gioia", "The recipient books the session at Via Tre Fontane 37, Catanzaro."],
    ],
    whyT: "A gift idea in Catanzaro, for every occasion",
    why: [
      "Looking for an original Christmas gift in Catanzaro? A beauty centre gift card is something to experience, not to forget in a drawer. The Glow Package pairs a professional facial treatment with a skincare product for every day.",
      "It is also perfect for Valentine's Day, a birthday, Mother's Day or to thank a special friend. Gioia welcomes you to her beauty centre in Catanzaro, Via Tre Fontane 37.",
    ],
    tags: ["Christmas gift Catanzaro", "Valentine's gift for her", "Beauty centre gift card", "Facial treatment Catanzaro", "Mesoporation Catanzaro", "Gift idea for women"],
    faqT: "Frequently asked questions",
    faq: [
      ["What does the Glow Package include?", "One aesthetic mesoporation session and the Gioia Skincare orange & vitamin C face cream."],
      ["How do I receive the gift card?", "After paying online you instantly receive the gift card code, with the recipient's name and your message."],
      ["Where can it be used?", "At Gioia Centro Estetico, Via Tre Fontane 37, Catanzaro. The recipient can book online or on WhatsApp."],
      ["Can I choose another treatment?", "Of course: in the gift card section you can choose any treatment or leave the choice to the recipient."],
    ],
    shareT: "Share the Glow Package", shareS: "Send it to whoever is buying you a gift, or to a friend looking for an idea.",
    copy: "Copy link", copied: "Link copied", share: "Share",
    shareText: "Look at the Glow Package from Gioia Centro Estetico in Catanzaro: Mesoporation + vitamin C cream, the perfect gift!",
    other: "All gift cards",
    seoT: { xmas: "Christmas gift in Catanzaro · Glow Package · Gioia", valentine: "Valentine's gift in Catanzaro · Glow Package · Gioia", none: "Gift idea in Catanzaro · Glow Package · Gioia" },
    seoD: "Glow Package: aesthetic mesoporation + vitamin C cream in one gift card. The Christmas and Valentine's gift idea from Gioia beauty centre in Catanzaro.",
  },
};

const btn = "inline-flex justify-center items-center gap-2 rounded-full px-7 py-4 text-[0.72rem] tracking-[0.2em] uppercase transition-colors duration-500";

const Hero = ({ c, season, onGive }) => (
  <div className="grid lg:grid-cols-12 gap-12 lg:gap-14 items-center">
    <div className="lg:col-span-7">
      <p className="overline text-[#8A6A3A] flex items-center gap-2"><Sparkles className="w-4 h-4" strokeWidth={1.5} /> {c.kicker}</p>
      <h1 className="mt-6 font-serif-d font-light text-[2.9rem] sm:text-7xl lg:text-[5.4rem] leading-[0.95] text-[#14110F]" data-testid="glow-title">
        <MaskLines lines={[c.h1[season ?? "none"]]} delay={0.1} />
      </h1>
      <p className="mt-8 text-[#4f4841] font-light leading-relaxed max-w-xl text-base sm:text-lg" data-testid="glow-lead">{c.lead}</p>
      <div className="mt-10 flex flex-col sm:flex-row gap-4">
        <button type="button" onClick={onGive} data-testid="glow-give-btn" className={`group ${btn} bg-[#14110F] text-[#F6F2EC] hover:bg-[#8A6A3A]`}>
          <Gift className="w-4 h-4" /> {c.give} <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
        </button>
        <a href={`${WHATSAPP}?text=${encodeURIComponent("Ciao Gioia, vorrei regalare il Pacchetto Glow")}`} target="_blank" rel="noreferrer" data-testid="glow-whatsapp-btn" className={`${btn} border border-[#14110F]/25 text-[#14110F] hover:border-[#8A6A3A] hover:text-[#8A6A3A]`}>
          <MessageCircle className="w-4 h-4" /> {c.wa}
        </a>
      </div>
    </div>
    <div className="lg:col-span-5 relative pb-10">
      <motion.div initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, ease }} className="relative overflow-hidden aspect-[4/5] w-[86%] rounded-t-[999px] bg-[#14110F]">
        <img src="/images/mesoporazione-estetica.jpg" alt="Mesoporazione estetica viso da Gioia Centro Estetico Catanzaro" className="absolute inset-0 w-full h-full object-cover" data-testid="glow-hero-image" />
      </motion.div>
      <div className="absolute right-0 bottom-0 w-[42%] aspect-square overflow-hidden border-4 border-[#F6F2EC] shadow-[0_30px_60px_-30px_rgba(20,17,15,0.6)]">
        <img src="/images/crema-vitc-hero.jpg" alt="Crema viso arancia e vitamina C Gioia Skincare" className="w-full h-full object-cover" />
      </div>
    </div>
  </div>
);

const Inside = ({ c }) => (
  <div>
    <Reveal><h2 className="font-serif-d font-light text-4xl sm:text-5xl text-[#14110F] leading-[1]">{c.inside}</h2></Reveal>
    <div className="mt-12 grid md:grid-cols-2 gap-6">
      {c.items.map((x, i) => (
        <Reveal key={x.t} delay={i * 0.08} className="border border-[#ddd4c7] bg-[#FBF8F3] p-6 sm:p-8 flex gap-6 items-start" >
          <img src={x.img} alt={x.t} className="w-20 h-24 sm:w-24 sm:h-28 object-cover rounded-t-full shrink-0" />
          <div>
            <span className="font-stencil tracking-[0.25em] text-[#8A6A3A]">+0{i + 1}</span>
            <h3 className="mt-2 font-serif-d text-2xl sm:text-3xl font-light text-[#14110F]">{x.t}</h3>
            <p className="mt-3 text-sm text-[#4f4841] font-light leading-relaxed">{x.d}</p>
            <Link to={x.to} data-testid={`glow-item-link-${i}`} className="mt-4 inline-flex items-center gap-1 text-[0.7rem] tracking-[0.2em] uppercase text-[#8A6A3A] underline underline-offset-8 hover:text-[#14110F] transition-colors duration-300">
              {x.l} <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </Reveal>
      ))}
    </div>
  </div>
);

const Steps = ({ c }) => (
  <div className="bg-[#14110F] grain text-[#F6F2EC] -mx-5 sm:mx-0 px-6 sm:px-12 lg:px-16 py-16 sm:py-20">
    <Reveal><h2 className="font-serif-d font-light text-4xl sm:text-5xl leading-[1]">{c.how}</h2></Reveal>
    <ol className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-10" data-testid="glow-steps">
      {c.steps.map(([t, d], i) => (
        <Reveal as="li" key={t} delay={i * 0.08} className="border-t border-[#F6F2EC]/20 pt-6">
          <span className="font-stencil tracking-[0.25em] text-[#C9A66B]">0{i + 1}</span>
          <h3 className="mt-4 font-serif-d text-2xl sm:text-3xl font-light">{t}</h3>
          <p className="mt-3 text-sm text-[#d9d0c4] font-light leading-relaxed">{d}</p>
        </Reveal>
      ))}
    </ol>
  </div>
);

const Why = ({ c }) => (
  <div className="grid lg:grid-cols-12 gap-8">
    <Reveal className="lg:col-span-5"><h2 className="font-serif-d font-light text-4xl sm:text-5xl text-[#14110F] leading-[1]">{c.whyT}</h2></Reveal>
    <Reveal delay={0.08} className="lg:col-span-7 space-y-5 text-[#4f4841] font-light leading-relaxed">
      {c.why.map((p) => <p key={p.slice(0, 20)}>{p}</p>)}
      <ul className="pt-3 flex flex-wrap gap-2" data-testid="glow-tags">
        {c.tags.map((t) => <li key={t} className="text-xs px-3 py-1.5 rounded-full border border-[#ddd4c7] text-[#4f4841]">{t}</li>)}
      </ul>
    </Reveal>
  </div>
);

const Faq = ({ c }) => (
  <div className="grid lg:grid-cols-12 gap-8">
    <Reveal className="lg:col-span-4"><h2 className="font-serif-d font-light text-4xl sm:text-5xl text-[#14110F] leading-[1]">{c.faqT}</h2></Reveal>
    <Accordion type="single" collapsible className="lg:col-span-8 border-t border-[#ddd4c7]" data-testid="glow-faq">
      {c.faq.map(([q, a], i) => (
        <AccordionItem key={q} value={`q${i}`} className="border-[#ddd4c7]">
          <AccordionTrigger data-testid={`glow-faq-trigger-${i}`} className="py-6 text-left font-serif-d font-light text-xl sm:text-2xl text-[#14110F] hover:no-underline hover:text-[#8A6A3A] transition-colors duration-300">{q}</AccordionTrigger>
          <AccordionContent className="text-base text-[#4f4841] font-light leading-relaxed pr-8">{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </div>
);

const ShareBar = ({ c, onGive }) => {
  const [busy, setBusy] = useState(false);
  const url = `${API}/api/share/glow`;
  const text = `${c.shareText} ${url}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success(c.copied); } catch { toast.error(url); }
  };
  const native = async () => {
    if (!navigator.share) { copy(); return; }
    setBusy(true);
    try { await navigator.share({ title: "Pacchetto Glow · Gioia", text: c.shareText, url }); } catch { /* annullato */ }
    setBusy(false);
  };
  const s = `${btn} border border-[#14110F]/20 text-[#14110F] hover:border-[#8A6A3A] hover:text-[#8A6A3A] px-5 py-3`;
  return (
    <div className="border border-[#ddd4c7] p-8 sm:p-14 flex flex-col lg:flex-row lg:items-end justify-between gap-10" data-testid="glow-share">
      <div className="max-w-xl">
        <h2 className="font-serif-d font-light text-4xl sm:text-5xl text-[#14110F] leading-[1]">{c.shareT}</h2>
        <p className="mt-4 text-[#4f4841] font-light">{c.shareS}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer" data-testid="glow-share-whatsapp" className={s}><MessageCircle className="w-4 h-4" /> WhatsApp</a>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer" data-testid="glow-share-facebook" className={s}><Facebook className="w-4 h-4" /> Facebook</a>
          <button type="button" onClick={copy} data-testid="glow-share-copy" className={s}><Link2 className="w-4 h-4" /> {c.copy}</button>
          <button type="button" onClick={native} disabled={busy} data-testid="glow-share-native" className={s}><Share2 className="w-4 h-4" /> {c.share}</button>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-4 shrink-0">
        <button type="button" onClick={onGive} data-testid="glow-cta-give-btn" className={`group ${btn} bg-[#C9A66B] text-[#14110F] font-medium hover:bg-[#14110F] hover:text-[#F6F2EC]`}>
          {c.give} <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
        </button>
        <Link to="/#gift" data-testid="glow-all-gifts-link" className={`${btn} border border-[#14110F]/25 text-[#14110F] hover:border-[#8A6A3A] hover:text-[#8A6A3A]`}>
          <Gift className="w-4 h-4" /> {c.other}
        </Link>
      </div>
    </div>
  );
};

export default function GlowGift() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const c = COPY[lang] ?? COPY.it;
  const season = getSeason();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useSeo(c.seoT[season ?? "none"], c.seoD);
  const onGive = () => navigate("/#gift", { state: { giftPackage: "glow" } });
  return (
    <PageShell>
      <div className="max-w-[1280px] mx-auto space-y-24 sm:space-y-32" data-testid="glow-page">
        <Hero c={c} season={season} onGive={onGive} />
        <Inside c={c} />
        <Steps c={c} />
        <Why c={c} />
        <Faq c={c} />
        <ShareBar c={c} onGive={onGive} />
      </div>
    </PageShell>
  );
}
